/**
 * Extension Stats Tab
 * Click tracking analytics from the Wendigo browser extension
 */

import { useState } from 'react';
import { Row, Col, Card, Table, Empty, Alert, Skeleton, Segmented, Tag } from 'antd';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useExtensionStats } from '@/hooks/useProjectStats';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { CHART_COLORS } from '@/styles/theme';
import { formatNumber } from '@/utils/formatters';
import { ActivityHeatmap } from './ActivityHeatmap';

const COLORS = [CHART_COLORS.primary, CHART_COLORS.success, CHART_COLORS.warning, CHART_COLORS.purple, CHART_COLORS.cyan, CHART_COLORS.magenta, CHART_COLORS.orange, CHART_COLORS.error];

export function ExtensionTab() {
  const { data, isLoading, isError, error } = useExtensionStats();
  const isDark = usePreferencesStore((s) => s.theme) === 'dark';
  const [clicksTimeRange, setClicksTimeRange] = useState<'last30d' | 'allTime'>('last30d');

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[1, 2, 3].map((i) => <Card key={i} size="small"><Skeleton active paragraph={{ rows: 4 }} /></Card>)}
    </div>
  );
  if (isError) return <Alert type="error" message="Failed to load extension stats" description={(error as Error)?.message} showIcon />;
  if (!data) return <Empty description="Extension database not connected" />;

  const gridColor = isDark ? '#303030' : '#f0f0f0';
  const textColor = isDark ? '#8c8c8c' : '#595959';
  const bgColor = isDark ? '#1f1f1f' : '#fff';

  const tooltipStyle = { backgroundColor: bgColor, border: `1px solid ${gridColor}`, borderRadius: 8 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Daily Volume */}
      <Card title="Daily Click Volume (90 days)" size="small">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.dailyVolume} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Clicks']} labelFormatter={(l) => l} />
            <Line type="monotone" dataKey="count" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Activity Heatmap */}
      <Card title="Activity Heatmap (90 days)" size="small">
        <ActivityHeatmap data={data.heatmap} color="blue" isDark={isDark} />
      </Card>

      <Row gutter={16}>
        {/* Clicks per User */}
        <Col xs={24} lg={12}>
          <Card
            title="Clicks per User"
            size="small"
            extra={
              <Segmented
                size="small"
                value={clicksTimeRange}
                onChange={(v) => setClicksTimeRange(v as 'last30d' | 'allTime')}
                options={[
                  { label: '30 Days', value: 'last30d' },
                  { label: 'All Time', value: 'allTime' },
                ]}
              />
            }
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.clicksPerUser[clicksTimeRange]} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                <XAxis type="number" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <YAxis type="category" dataKey="name" tick={{ fill: textColor, fontSize: 11 }} width={55} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Clicks']} />
                <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Unique URLs per Day */}
        <Col xs={24} lg={12}>
          <Card title="Unique URLs per Day (90 days)" size="small">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.uniqueUrlsPerDay} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Unique URLs']} />
                <Line type="monotone" dataKey="count" stroke={CHART_COLORS.success} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Per-User Sparklines */}
      <Card title="Per-User Daily Activity (30 days)" size="small">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.perUserSparklines.dates.map((date, i) => {
            const point: Record<string, any> = { date };
            data.perUserSparklines.users.forEach((u) => { point[u.name] = u.data[i]; });
            return point;
          })} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            {data.perUserSparklines.users.map((u, i) => (
              <Line key={u.name} type="monotone" dataKey={u.name} stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Row gutter={16}>
        {/* Top Domains */}
        <Col xs={24} lg={12}>
          <Card title="Top Destination Domains" size="small">
            <Table
              dataSource={data.topDomains.slice(0, 20)}
              rowKey="domain"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Domain', dataIndex: 'domain', key: 'domain', ellipsis: true },
                { title: 'Clicks', dataIndex: 'count', key: 'count', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              ]}
            />
          </Card>
        </Col>

        {/* Top Origin Domains */}
        <Col xs={24} lg={12}>
          <Card title="Top Origin Domains" size="small">
            <Table
              dataSource={data.topOriginDomains.slice(0, 20)}
              rowKey="domain"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Domain', dataIndex: 'domain', key: 'domain', ellipsis: true },
                { title: 'Clicks', dataIndex: 'count', key: 'count', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Users Table */}
      <Card title="Extension Users" size="small">
        <Table
          dataSource={data.usersTable}
          rowKey="email"
          size="small"
          pagination={false}
          columns={[
            { title: 'Name', dataIndex: 'name', key: 'name' },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Total Clicks', dataIndex: 'totalClicks', key: 'totalClicks', align: 'right' as const, sorter: (a, b) => a.totalClicks - b.totalClicks, render: (v: number) => formatNumber(v) },
            { title: 'Joined', dataIndex: 'createdAt', key: 'createdAt', render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' },
            { title: 'Last Login', dataIndex: 'lastLogin', key: 'lastLogin', render: (v: string) => v ? new Date(v).toLocaleDateString() : <Tag color="orange">Never</Tag> },
          ]}
        />
      </Card>
    </div>
  );
}
